import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, CheckCircle2, Phone, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { services } from '../data/mock';
import * as Icons from 'lucide-react';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Service introuvable</h1>
          <Link to="/services">
            <Button>Retour aux services</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = Icons[service.icon];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{service.title} - Nettoyage par drone | PULVISTAR</title>
        <meta name="description" content={service.description} />
        <link rel="canonical" href={`https://pulvistar.fr/services/${service.slug}`} />
      {/* Hero Section */}
      </Helmet>
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16">
        <div className="container mx-auto px-4">
          <Link to="/services" className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Retour aux services
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center">
                  <IconComponent className="w-9 h-9 text-blue-900" />
                </div>
                <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-200 border-0">
                  Service professionnel
                </Badge>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
                {service.title}
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                {service.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => window.location.href = '/devis'}>
                  <FileText className="w-5 h-5" />
                  Demander un devis
                </Button>
                <Button size="lg" variant="outline" className="gap-2" onClick={() => window.location.href = 'tel:+33184180342'}>
                  <Phone className="w-5 h-5" />
                  Appeler
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl transform rotate-3 opacity-10"></div>
              <img
                src={service.image}
                alt={service.title}
                className="relative z-10 rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Les avantages de notre service
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.benefits.map((benefit, index) => (
              <Card key={index} className="p-6 border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-gray-700 font-medium" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                    {benefit}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Notre processus d'intervention
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {service.process.map((step, index) => (
              <div key={index} className="relative">
                <Card className="p-6 border-gray-200 h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {index + 1}
                  </div>
                  <p className="text-gray-700 font-medium" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                    {step}
                  </p>
                </Card>
                {index < service.process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <Icons.ArrowRight className="w-8 h-8 text-cyan-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Note Section */}
      <section className="py-16 lg:py-24 bg-blue-50">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto p-8 border-blue-200 bg-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icons.Info className="w-6 h-6 text-blue-900" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Tarification sur devis — à partir de 5.90€/m²
                </h3>
                <p className="text-gray-600 mb-4" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  Chaque intervention est unique. Nos tarifs varient selon plusieurs critères :
                </p>
                <ul className="space-y-2 text-gray-600" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500 font-bold">•</span>
                    Surface à traiter et type de support
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500 font-bold">•</span>
                    Degré d'encrassement et complexité d'accès
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500 font-bold">•</span>
                    Contraintes de sécurité et environnementales
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500 font-bold">•</span>
                    Localisation et urgence de l'intervention
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4 italic">
                  Demandez votre devis personnalisé gratuit pour obtenir un tarif précis adapté à votre projet.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
            Contactez-nous pour une évaluation gratuite et un devis détaillé
          </p>
          <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => window.location.href = '/devis'}>
            Obtenir un devis gratuit <Icons.ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
